import type React from 'react';
import { useState } from 'react';

import {
	AccordionBodyHtml,
	AccordionBodyText,
	AccordionHeaderIcon,
	AccordionHeaderText,
	AccordionHeaderTextLeft,
	AccordionHeaderTextNumber,
	AccordionRow,
	AccordionRowBody,
	AccordionRowHeader,
	AccordionSection,
	HeaderBodyText,
	HeaderContainer,
} from './styled';

export const PAGE_SIZE = 10;

interface IFAQ {
	ye?: any;
}

const FAQ: React.FC<IFAQ> = () => {
	const instructiondata = [
		{
			id: 1,
			header: 'How do I Volunteer ?',
			answer: `<p>To volunteer, prospective Agents need to access the party agent registration page by clicking on the "VOLUNTEER AS PARTY AGENT" or "VOLUNTEER NOW' buttons highlighted in black. Volunteers can also access the registration page by clicking on the Register button located at the top right corner of the landing page.</p>

      <p>Volunteers should take note of the Election titled on this page as that is the election they will be registering for. If the election titled is not the election of interest, the volunteer should contact the party admin to provide the link to register for the election that is of interest to them.</p>

      <p>After successfully accessing the party registration page, volunteers must submit their personal information and then navigate to the polling unit for which they wish to be an agent.</p>

      <p>The Steps to get this done are highlighted below;</p>
      <ul>
        <li><b>Step 1:</b> Go to the Party Registration page by clicking the "VOLUNTEER AS PARTY AGENT" or "VOLUNTEER NOW" buttons or the "Register" button in the upper right corner of the landing page.</li>
        <li><b>Step 2:</b> Enter your title, first name, middle name, last name, membership number (optional), email address, and phone number. Upload a recent photo of yourself that clearly shows your face.</li>
        <li><b>Step 3:</b> After entering your basic information, click the "NEXT" button at the bottom of the form to go to the Polling unit information page.</li>
        <li><b>Step 4:</b> Navigate to the polling unit of interest on the Polling unit information page by selecting the State, Local Government Area, and Ward in which the polling unit is located.</li>
        <li><b>Step 5:</b> After selecting the polling unit, click "NEXT" to go to the summary page.</li>
        <li><b>Step 6:</b> On the summary page, check to see if the details submitted are correct and make any necessary changes by clicking on the change tag in the top right corner of each form data.</li>
        <li><b>Step 7:</b> If all of your submitted data is correct, click "SUBMIT" to submit your request.</li>
      </ul>
      <p>After request submission, Volunteers will be contacted with their login details when their request is approved.</p>`,
			isHtml: true,
		},
		{
			id: 2,
			header: 'How do I locate my polling unit ?',
			answer: `<p>You can locate your polling unit using the following methods:</p>
      <ul>
        <li><b>INEC Voter Portal:</b> Visit the Independent National Electoral Commission (INEC) website at <a href="https://voters.inecnigeria.org" target="_blank" rel="noopener noreferrer">voters.inecnigeria.org</a> and use your Voter Identification Number (VIN) to find your registered polling unit.</li>
        <li><b>INEC Mobile App:</b> Download the INEC mobile application from Google Play Store or Apple App Store to check your polling unit details.</li>
        <li><b>SMS Service:</b> Send your VIN to the INEC SMS line to receive information about your polling unit location.</li>
        <li><b>Local INEC Office:</b> Visit your local INEC office with your voter's card for assistance in locating your polling unit.</li>
      </ul>
      <p>When registering as a volunteer agent, you will need to know your State, Local Government Area (LGA), Ward, and specific Polling Unit name or code.</p>`,
			isHtml: true,
		},
		{
			id: 3,
			header: 'Is there going to be a formal training to get us prepared for the job ?',
			answer: `<p>Yes, all approved volunteer agents will receive comprehensive training before election day. The training program includes:</p>
      <ul>
        <li><b>Online Training Modules:</b> Access to digital learning materials covering election procedures, agent responsibilities, and legal requirements.</li>
        <li><b>In-Person Sessions:</b> Physical training sessions conducted in your area to practice real-world scenarios and ask questions directly to trainers.</li>
        <li><b>Training Materials:</b> You will receive a handbook and reference materials outlining your duties, the voting process, and how to handle various situations.</li>
        <li><b>Mock Elections:</b> Participation in simulated election exercises to ensure you are fully prepared for the actual event.</li>
      </ul>
      <p>Training schedules will be communicated to approved volunteers via email and SMS. Attendance is mandatory to be certified as an election agent.</p>
      <p><b>Note:</b> Volunteers who complete training will receive a certificate of completion and will be eligible for compensation on election day.</p>`,
			isHtml: true,
		},
	];

	const [openAccordion, setOpenAccordion] = useState(0);

	return (
		<div className='container'>
			<HeaderContainer>
				<HeaderBodyText>FAQ</HeaderBodyText>
			</HeaderContainer>
			<AccordionSection>
				{instructiondata.map((i, index) => (
					<AccordionPageSection
						answer={i.answer}
						header={i.header}
						id={i.id}
						key={i.id}
						index={index + 1}
						noborder={index === instructiondata.length - 1 ? 'true' : 'false'}
						setOpenAccordion={setOpenAccordion}
						openAccordion={openAccordion}
						isHtml={i.isHtml}
					/>
				))}
			</AccordionSection>
		</div>
	);
};

export default FAQ;

const AccordionPageSection = ({
	id,
	header,
	answer,
	noborder,
	openAccordion,
	setOpenAccordion,
	index,
	isHtml,
}: {
	id: number;
	header: string;
	answer: string;
	noborder: 'true' | 'false';
	setOpenAccordion: React.Dispatch<React.SetStateAction<number>>;
	openAccordion: number;
	index: number;
	isHtml: boolean;
}) => {
	const isId = openAccordion === id;
	return (
		<AccordionRow noborder={noborder}>
			<AccordionRowHeader onClick={() => setOpenAccordion(isId ? 0 : id)}>
				<AccordionHeaderTextLeft>
					<AccordionHeaderTextNumber>{index}</AccordionHeaderTextNumber>
					<AccordionHeaderText>{header}</AccordionHeaderText>
				</AccordionHeaderTextLeft>
				<AccordionHeaderIcon className={isId ? 'fas fa-minus' : 'fas fa-plus'} />
			</AccordionRowHeader>
			{isId && (
				<AccordionRowBody>
					{isHtml ? (
						<AccordionBodyHtml dangerouslySetInnerHTML={{ __html: answer }} />
					) : (
						<AccordionBodyText>{answer}</AccordionBodyText>
					)}
				</AccordionRowBody>
			)}
		</AccordionRow>
	);
};
